import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useState } from 'react';
import { Box, useTheme, css, useBreakpoint, useBreakpointValue, Stack } from '@chakra-ui/react';
import { IconButton } from '@outdoorly/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@outdoorly/icons';
import propTypes from 'prop-types';
import SwiperCore, { Navigation, Mousewheel, Thumbs, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

var _excluded = ["marginOverflowCss", "centered"],
    _excluded2 = ["slidesPerView", "children", "spacingPx", "marginOverflowCss", "centered", "maxWidths", "buttonPadding", "hasOverflowingFrames", "isSmallScreen", "linkedSwiper", "onSwiper", "loop", "hideButtons", "pagination", "id"],
    _excluded3 = ["children", "visibleFrames", "spacingPx", "marginOverflow", "buttonPadding", "peekWidth", "centered", "linkedSwiper", "onSwiper", "loop", "hideButtons", "pagination"];
SwiperCore.use([Navigation, Mousewheel, Thumbs, Pagination]);
var SwiperWrapper = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var marginOverflowCss = _ref.marginOverflowCss,
      centered = _ref.centered,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(Box, _extends({
    ref: ref,
    width: "calc(100% + calc(".concat(marginOverflowCss, " * ").concat(centered ? '2' : '1', "))"),
    marginLeft: centered ? "calc(".concat(marginOverflowCss, " * -1)") : 0
  }, props));
});

if (process.env.NODE_ENV === 'development') {
  SwiperWrapper.displayName = 'SwiperWrapper';
}

SwiperWrapper.propTypes = {
  /**
   * Used to overflow the filmstrip outside the container with a fade effect
   */
  marginOverflowCss: propTypes.string.isRequired,

  /**
   * If centered, negative left margin is used to show overflow on the left. If
   * not centered, no negative left margin is used
   */
  centered: propTypes.bool
};
var SwiperFilmstrip = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var slidesPerView = _ref2.slidesPerView,
      children = _ref2.children,
      spacingPx = _ref2.spacingPx,
      marginOverflowCss = _ref2.marginOverflowCss,
      centered = _ref2.centered,
      maxWidths = _ref2.maxWidths,
      buttonPadding = _ref2.buttonPadding,
      hasOverflowingFrames = _ref2.hasOverflowingFrames,
      isSmallScreen = _ref2.isSmallScreen,
      linkedSwiper = _ref2.linkedSwiper,
      _ref2$onSwiper = _ref2.onSwiper,
      onSwiper = _ref2$onSwiper === void 0 ? function () {} : _ref2$onSwiper,
      loop = _ref2.loop,
      hideButtons = _ref2.hideButtons,
      pagination = _ref2.pagination,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? 'filmstrip' : _ref2$id,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var _useState = useState({
    isBeginning: true,
    isEnd: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      navigationState = _useState2[0],
      setNavigationState = _useState2[1];

  var theme = useTheme();
  var buttonHalfWidth = '1.5rem';
  var buttonPaddingCss = css({
    padding: buttonPadding
  })(theme).padding; // Stick to the edge + 1rem of padding. Also takes into account any extra
  // width due to overflow, and adds that to the padding.

  var leftButtonPadding = buttonPaddingCss; // When not centered (ie; left aligned), there's no extra space needed on
  // the left

  if (centered && marginOverflowCss) {
    leftButtonPadding = "calc(".concat(buttonPaddingCss, " + ").concat(marginOverflowCss, ")");
  }

  var rightButtonPadding = buttonPaddingCss;

  if (marginOverflowCss) {
    rightButtonPadding = "calc(".concat(buttonPaddingCss, " + ").concat(marginOverflowCss, ")");
  } // By default we don't render buttons


  var renderButtons = false; // But if we have more frames than can be visible, we'll render them then

  if (hasOverflowingFrames || loop) {
    renderButtons = true;
  } // If it's a small screen, we'll hide them


  if (isSmallScreen) {
    renderButtons = false;
  } // Additionally we can force hide them by passing in this prop


  if (hideButtons) {
    renderButtons = false;
  }

  var renderLeftButton = renderButtons;
  var renderRightButton = renderButtons;

  if (!loop) {
    if (navigationState.isBeginning) renderLeftButton = false;
    if (navigationState.isEnd) renderRightButton = false;
  }
  /* NOTE: left: -2px is to cover some pixel rounding by Swiper which
   * leaves a pixel strip visible right at the edges at some sizes
   */


  return /*#__PURE__*/React.createElement(Box, props, /*#__PURE__*/React.createElement(IconButton, {
    size: "lg",
    isRound: true,
    paddingX: 3,
    icon: /*#__PURE__*/React.createElement(ChevronLeftIcon, {
      width: 6,
      height: 6
    }),
    variant: "solid",
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translate(100%, -50%)",
    zIndex: "10",
    id: "swiper-prev-button-".concat(id),
    "aria-label": "Previous",
    boxShadow: "md",
    display: renderLeftButton ? undefined : 'none',
    backgroundColor: "scheme.bgPrimary"
  }), /*#__PURE__*/React.createElement(IconButton, {
    size: "lg",
    isRound: true,
    paddingX: 3,
    icon: /*#__PURE__*/React.createElement(ChevronRightIcon, {
      width: 6,
      height: 6
    }),
    variant: "solid",
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translate(-100%, -50%)",
    zIndex: "10",
    id: "swiper-next-button-".concat(id),
    "aria-label": "Next",
    boxShadow: "md",
    display: renderRightButton ? undefined : 'none',
    backgroundColor: "scheme.bgPrimary"
  }), /*#__PURE__*/React.createElement(Swiper, {
    ref: ref,
    tag: SwiperWrapper,
    marginOverflowCss: marginOverflowCss,
    spaceBetween: spacingPx,
    slidesPerView: slidesPerView,
    loop: loop,
    centered: centered,
    centeredSlides: centered,
    centerInsufficientSlides: true,
    watchSlidesVisibility: true,
    watchSlidesProgress: true,
    navigation: {
      nextEl: "#swiper-next-button-".concat(id),
      prevEl: "#swiper-prev-button-".concat(id)
    },
    mousewheel: {
      forceToAxis: true
    },
    thumbs: {
      swiper: linkedSwiper
    },
    onSwiper: onSwiper,
    onSlideChange: function onSlideChange(swiper) {
      setNavigationState({
        isBeginning: swiper.isBeginning,
        isEnd: swiper.isEnd
      });
    },
    pagination: pagination ? pagination : false
  }, /*#__PURE__*/React.createElement(Box, {
    width: "calc(".concat(leftButtonPadding, " + ").concat(buttonHalfWidth, ")"),
    height: "full",
    background: "linear-gradient(-90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
    position: "absolute",
    left: "-2px",
    top: 0,
    zIndex: "10",
    pointerEvents: "none",
    display: renderLeftButton ? undefined : 'none'
  }), /*#__PURE__*/React.createElement(Box, {
    width: "calc(".concat(rightButtonPadding, " + ").concat(buttonHalfWidth, ")"),
    height: "full",
    background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
    position: "absolute",
    right: "-1px",
    top: 0,
    zIndex: "10",
    pointerEvents: "none",
    display: renderRightButton ? undefined : 'none'
  }), React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.createElement(SwiperSlide, {
      tag: Box,
      key: index,
      maxW: maxWidths
    }, child);
  })));
});

if (process.env.NODE_ENV !== 'production') {
  SwiperFilmstrip.displayName = 'SwiperFilmstrip';
}

var Filmstrip = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
      visibleFrames = _ref3.visibleFrames,
      _ref3$spacingPx = _ref3.spacingPx,
      spacingPx = _ref3$spacingPx === void 0 ? 12 : _ref3$spacingPx,
      _ref3$marginOverflow = _ref3.marginOverflow,
      marginOverflow = _ref3$marginOverflow === void 0 ? 6 : _ref3$marginOverflow,
      _ref3$buttonPadding = _ref3.buttonPadding,
      buttonPadding = _ref3$buttonPadding === void 0 ? 4 : _ref3$buttonPadding,
      _ref3$peekWidth = _ref3.peekWidth,
      peekWidth = _ref3$peekWidth === void 0 ? 6 : _ref3$peekWidth,
      _ref3$centered = _ref3.centered,
      centered = _ref3$centered === void 0 ? false : _ref3$centered,
      linkedSwiper = _ref3.linkedSwiper,
      onSwiper = _ref3.onSwiper,
      _ref3$loop = _ref3.loop,
      loop = _ref3$loop === void 0 ? true : _ref3$loop,
      _ref3$hideButtons = _ref3.hideButtons,
      hideButtons = _ref3$hideButtons === void 0 ? false : _ref3$hideButtons,
      pagination = _ref3.pagination,
      props = _objectWithoutProperties(_ref3, _excluded3);

  var theme = useTheme();
  var breakpoint = useBreakpoint('sm');
  var isSmallScreen = ['base', 'sm'].includes(breakpoint);
  var visibleFramesArray = Array.isArray(visibleFrames) ? visibleFrames : [visibleFrames];
  var visibleFramesForBreakpoint = useBreakpointValue(visibleFramesArray);
  var totalFrames = React.Children.count(children);
  var hasOverflowingFrames = totalFrames > visibleFramesForBreakpoint;
  var marginOverflowCss = marginOverflow === 0 ? '0px' : css({
    margin: marginOverflow
  })(theme).margin;
  var peekWidthCss = marginOverflow === 0 ? '0px' : css({
    width: peekWidth
  })(theme).width; // An array of max widths which take into consideration both spacing and
  // visible frames

  var maxWidths = visibleFramesArray.map(function (frames) {
    // By default, spaces are _between_ frames, so there is one less space
    // than visible frames
    var spacesBetweenFrames = Math.min(frames, totalFrames) - 1; // When frames are peeking out from the sides, we need to account for
    // extra spacing.

    if (hasOverflowingFrames && peekWidthCss) {
      // When centered, there's an extra space on both left & right
      if (centered && !isSmallScreen) {
        spacesBetweenFrames += 2;
      } else {
        // When left-aligned (ie; not centered), there's only an extra space
        // on the right
        spacesBetweenFrames += 1;
      }
    }

    var spacingCalc = "calc(".concat(spacingPx, "px * ").concat(spacesBetweenFrames, ")"); // By default assume there's no margin overflow

    var marginOverflowCalc = '0px'; // Mobile has no overflow

    if (!isSmallScreen && hasOverflowingFrames) {
      // When centered, overflow is on both left & right
      if (centered) {
        marginOverflowCalc = "calc(".concat(marginOverflowCss, " * 2)");
      } else {
        // When left-aligned (ie; not centered), there's only overflow right
        marginOverflowCalc = marginOverflowCss;
      }
    } // By default assume there's no peeking


    var peekWidthCalc = '0px';

    if (hasOverflowingFrames && peekWidthCss) {
      // When centered, peeking on both left & right
      if (centered && !isSmallScreen) {
        peekWidthCalc = "calc(".concat(peekWidthCss, " * 2)");
      } else {
        // When left-aligned (ie; not centered), there's only overflow right
        peekWidthCalc = peekWidthCss;
      }
    } // Add up all the spacing


    var allTheSpacing = "calc(".concat(spacingCalc, " + calc(").concat(marginOverflowCalc, " + ").concat(peekWidthCalc, "))"); // And remove that from the total width

    var widthWithoutSpacing = "calc(100% - ".concat(allTheSpacing, ")"); // Finally, the size of each frame is calculated. Phew!

    return "calc(".concat(widthWithoutSpacing, " / ").concat(frames, ")");
  }); // In some situations it's better to skip using Swiper and just use a
  // standard element with horiztonal scrolling

  var skipCarousel = false; // When there's no overflowing elements, don't use Swiper

  skipCarousel = !hasOverflowingFrames; // When we're on a small screen, don't use Swiper

  skipCarousel = isSmallScreen; // When we use linkedSwiper or onSwiper, we must render a Swiper
  // instance, as these props are relying on it, this overrides everything
  // else

  if (linkedSwiper || onSwiper) {
    skipCarousel = false;
  }

  if (skipCarousel) {
    return /*#__PURE__*/React.createElement(Stack, {
      ref: ref,
      direction: "row",
      justify: isSmallScreen ? 'left' : 'center',
      spacing: "".concat(spacingPx, "px"),
      overflowX: "scroll",
      width: "full"
    }, React.Children.map(children, function (child, index) {
      return /*#__PURE__*/React.cloneElement(child, {
        minWidth: maxWidths,
        width: maxWidths,
        id: index
      });
    }));
  }

  return /*#__PURE__*/React.createElement(SwiperFilmstrip, _extends({
    ref: ref,
    spacingPx: spacingPx,
    marginOverflowCss: marginOverflowCss,
    centered: centered,
    maxWidths: maxWidths,
    buttonPadding: buttonPadding,
    hasOverflowingFrames: hasOverflowingFrames,
    isSmallScreen: isSmallScreen,
    linkedSwiper: linkedSwiper,
    onSwiper: onSwiper,
    loop: loop,
    slidesPerView: "auto",
    hideButtons: hideButtons,
    pagination: pagination
  }, props), children);
});

if (process.env.NODE_ENV !== 'production') {
  Filmstrip.displayName = 'Filmstrip';
}

Filmstrip.propTypes = {
  children: propTypes.node.isRequired,

  /**
   * Children will be given a `width` prop which ensures this number of children
   * are visible at one time. Excess children will overflow & fade to the sides.
   * If there are not enough children to fill the visible frames, remaining
   * items will be centered.
   */
  visibleFrames: propTypes.oneOfType([propTypes.number, propTypes.arrayOf(propTypes.number)]),

  /**
   * Spacing between each frame. Must be a pixel value.
   */
  spacingPx: propTypes.number,

  /**
   * Distance between container edge and button edge.
   * The overflow fade-out will start at the button's center point.
   */
  buttonPadding: propTypes.number,

  /**
   * How far adjacent frames overflow outside the edges of the filmstrip. Uses
   * the `theme.space` scale.
   */
  marginOverflow: propTypes.number,

  /**
   * How much of adjacent frames should peek out. Uses the `theme.space` scale.
   */
  peekWidth: propTypes.number,

  /**
   * Are slides centered? If not, left-aligned. Ignored for breakpoint 'sm' and
   * below.
   */
  centered: propTypes.bool,

  /**
   * Instance of another Swiper instance to link them together (to faciliate
   * things like thumbnails and a hero image)
   */
  linkedSwiper: propTypes.object,

  /**
   * When Swiper initialises this function is returned with the Swiper instance
   */
  onSwiper: propTypes.func,

  /**
   * Whether we want to loop the slides or have it end at the last image
   */
  loop: propTypes.bool,

  /**
   * Force the previous/next buttons to be hidden even if we have content not
   * visible
   */
  hideButtons: propTypes.bool,

  /**
   * Pagination options
   */
  pagination: propTypes.object
};
SwiperFilmstrip.propTypes = _objectSpread({}, Filmstrip.props);

export { Filmstrip };
